<template>

<section id="categorySection" class="level-section">

    <v-container>

        <!-- ============================= -->
        <!-- HEADER -->
        <!-- ============================= -->

        <div class="section-header">

            <div>

                <span class="section-label">
                    EXPLORE
                </span>

                <h2>
                    Categories
                </h2>

                <p class="section-description">
                    Encontrá figuras y movimientos según el tipo de técnica.
                </p>

            </div>

            <button
                v-if="categories.length > visibleLimit"
                class="see-all"
                @click="showAll = !showAll"
            >
                {{ showAll ? "Ver menos" : "Ver todas →" }}
            </button>

        </div>


        <!-- ============================= -->
        <!-- LOADING -->
        <!-- ============================= -->

        <div
            v-if="loading"
            class="category-state"
        >
            Cargando categorías...
        </div>


        <!-- ============================= -->
        <!-- CATEGORÍAS -->
        <!-- ============================= -->

        <div
            v-else-if="categories.length"
            class="category-grid"
        >

            <article
                v-for="category in visibleCategories"
                :key="category.id"
                class="category-card"
                @click="goToCategory(category)"
            >

                <!-- ICONO -->

                <div class="category-icon">

                    <v-icon>
                        {{ getCategoryIcon(category) }}
                    </v-icon>

                </div>


                <!-- INFORMACIÓN -->

                <div class="category-info">

                    <h3>
                        {{ getCategoryName(category) }}
                    </h3>

                    <p>
                        {{ getCategoryDescription(category) }}
                    </p>

                </div>


                <!-- CANTIDAD -->

                <div
                    v-if="getTrickCount(category) !== null"
                    class="category-count"
                >

                    <strong>
                        {{ getTrickCount(category) }}
                    </strong>

                    <span>
                        figuras
                    </span>

                </div>


                <!-- FLECHA -->

                <div class="category-arrow">
                    →
                </div>

            </article>

        </div>


        <!-- ============================= -->
        <!-- VACÍO -->
        <!-- ============================= -->

        <div
            v-else
            class="category-state"
        >
            No hay categorías disponibles.
        </div>


        <!-- ===================================== -->
        <!-- TRUCOS A-Z -->
        <!-- ===================================== -->

        <section class="az-section">

            <div class="section-header">

                <div>

                    <span class="section-label">
                        ALL MOVES
                    </span>

                    <h2>
                        From A to Z
                    </h2>

                    <p class="section-description">
                        Explore all pole tricks alphabetically.
                    </p>

                </div>

                <span
                    v-if="!loadingTricks"
                    class="az-count"
                >
                    {{ filteredTricks.length }} tricks
                </span>

            </div>


            <!-- ===================================== -->
            <!-- LETRAS -->
            <!-- ===================================== -->

            <div class="alphabet">

                <button
                    class="letter all-letter"
                    :class="{
                        active: selectedLetter === 'ALL'
                    }"
                    @click="selectLetter('ALL')"
                >
                    ALL
                </button>

                <button
                    v-for="letter in alphabet"
                    :key="letter"
                    class="letter"
                    :class="{
                        active: selectedLetter === letter,
                        disabled: !hasLetter(letter)
                    }"
                    :disabled="!hasLetter(letter)"
                    @click="selectLetter(letter)"
                >
                    {{ letter }}
                </button>

            </div>


            <!-- ===================================== -->
            <!-- LOADING A-Z -->
            <!-- ===================================== -->

            <div
                v-if="loadingTricks"
                class="category-state az-state"
            >
                Loading tricks...
            </div>


            <!-- ===================================== -->
            <!-- SIN RESULTADOS -->
            <!-- ===================================== -->

            <div
                v-else-if="filteredTricks.length === 0"
                class="category-state az-state"
            >
                No tricks found.
            </div>


            <!-- ===================================== -->
            <!-- CARRUSEL -->
            <!-- ===================================== -->

            <div
                v-else
                class="az-carousel"
            >

                <button
                    class="az-arrow az-left"
                    @click="scrollAZ(-1)"
                >
                    ❮
                </button>


                <div
                    ref="azSlider"
                    class="az-track"
                >

                    <article
                        v-for="trick in filteredTricks"
                        :key="trick.id"
                        class="az-card"
                        @click="goToTrick(trick)"
                    >

                        <!-- IMAGEN -->

                        <div class="az-image-wrapper">

                            <img
                                v-if="
                                    trick.portada &&
                                    trick.portada.ruta
                                "
                                :src="getStorageUrl(trick.portada.ruta)"
                                :alt="trick.nombre"
                                class="az-image"
                            >

                            <div
                                v-else
                                class="az-image no-image"
                            >
                                No image
                            </div>


                            <!-- NIVEL -->

                            <span
                                v-if="trick.dificultad"
                                class="difficulty"
                            >
                                {{ trick.dificultad.nombre }}
                            </span>

                        </div>


                        <!-- INFO -->

                        <div class="az-card-info">

                            <h3>
                                {{ trick.nombre }}
                            </h3>

                            <span
                                v-if="trick.categoria"
                                class="az-category"
                            >
                                {{ trick.categoria.nombre }}
                            </span>

                        </div>

                    </article>

                </div>


                <button
                    class="az-arrow az-right"
                    @click="scrollAZ(1)"
                >
                    ❯
                </button>

            </div>

        </section>

    </v-container>

</section>

</template>


<script>

import { urlBase } from "@/service/Api";
import TrucoService from "@/service/TrucoService";

export default {

    name: "CategorySection",

    data() {

        return {

            /*
             * ==============================
             * CATEGORÍAS
             * ==============================
             */

            categories: [],

            loading: true,

            showAll: false,

            visibleLimit: 8,


            /*
             * ==============================
             * A-Z
             * ==============================
             */

            tricks: [],

            loadingTricks: true,

            selectedLetter: "ALL",

            alphabet: [
                "A",
                "B",
                "C",
                "D",
                "E",
                "F",
                "G",
                "H",
                "I",
                "J",
                "K",
                "L",
                "M",
                "N",
                "O",
                "P",
                "Q",
                "R",
                "S",
                "T",
                "U",
                "V",
                "W",
                "X",
                "Y",
                "Z"
            ]

        };

    },


    computed: {

        /*
         * ==============================
         * CATEGORÍAS VISIBLES
         * ==============================
         */

        visibleCategories() {

            if (this.showAll) {

                return this.categories;

            }

            return this.categories.slice(
                0,
                this.visibleLimit
            );

        },


        /*
         * ==============================
         * TRUCOS FILTRADOS A-Z
         * ==============================
         */

        filteredTricks() {

            if (this.selectedLetter === "ALL") {

                return this.tricks;

            }

            return this.tricks.filter(trick => {

                if (!trick.nombre) {

                    return false;

                }

                return trick.nombre
                    .trim()
                    .toUpperCase()
                    .startsWith(
                        this.selectedLetter
                    );

            });

        }

    },


    mounted() {

        this.loadCategories();

        this.loadTricksAZ();

    },


    methods: {

        /*
         * ==============================
         * CATEGORÍAS API
         * ==============================
         */

        async loadCategories() {

            try {

                this.loading = true;

                const response =
                    await fetch(
                        `${urlBase}/categorias/pole`
                    );

                if (!response.ok) {

                    throw new Error(
                        "No se pudieron cargar las categorías"
                    );

                }

                const data =
                    await response.json();

                this.categories =
                    Array.isArray(data)
                        ? data
                        : data.data || [];

                console.log(
                    "CATEGORIAS:",
                    this.categories
                );

            } catch (error) {

                console.error(
                    "Error cargando categorías:",
                    error
                );

                this.categories = [];

            } finally {

                this.loading = false;

            }

        },


        /*
         * ==============================
         * A-Z API
         * ==============================
         */

        async loadTricksAZ() {

            try {

                this.loadingTricks = true;

                const response =
                    await TrucoService.obtenerTrucosAZ();

                const data =
                    response.data;

                this.tricks =
                    Array.isArray(data)
                        ? data
                        : data.data || [];

                console.log(
                    "TRUCOS A-Z:",
                    this.tricks
                );

            } catch (error) {

                console.error(
                    "Error cargando trucos A-Z:",
                    error
                );

                this.tricks = [];

            } finally {

                this.loadingTricks = false;

            }

        },


        /*
         * ==============================
         * FILTRO A-Z
         * ==============================
         */

        selectLetter(letter) {

            this.selectedLetter =
                letter;

            this.$nextTick(() => {

                if (this.$refs.azSlider) {

                    this.$refs.azSlider.scrollTo({
                        left: 0,
                        behavior: "smooth"
                    });

                }

            });

        },


        hasLetter(letter) {

            return this.tricks.some(trick => {

                if (!trick.nombre) {

                    return false;

                }

                return trick.nombre
                    .trim()
                    .toUpperCase()
                    .startsWith(letter);

            });

        },


        /*
         * ==============================
         * STORAGE
         * ==============================
         */

        getStorageUrl(path) {

            if (!path) {

                return "";

            }

            const backendUrl =
                urlBase.replace(
                    /\/api\/?$/,
                    ""
                );

            return `${backendUrl}/storage/${path}`;

        },


        /*
         * ==============================
         * IR AL TRUCO
         * ==============================
         */

        goToTrick(trick) {

            if (
                !trick ||
                !trick.slug
            ) {

                console.error(
                    "El truco no tiene slug:",
                    trick
                );

                return;

            }

            this.$router.push(
                `/trick/${trick.slug}`
            );

        },


        /*
         * ==============================
         * CARRUSEL A-Z
         * ==============================
         */

        scrollAZ(direction) {

            const slider =
                this.$refs.azSlider;

            if (!slider) {

                return;

            }

            slider.scrollBy({
                left: direction * 650,
                behavior: "smooth"
            });

        },


        /*
         * ==============================
         * NOMBRE
         * ==============================
         */

        getCategoryName(category) {

            if (
                category.traducciones &&
                category.traducciones.length
            ) {

                const english =
                    category.traducciones.find(
                        item =>
                            item.idioma === "en"
                    );

                if (
                    english &&
                    english.nombre
                ) {

                    return english.nombre;

                }

            }

            return (
                category.nombre ||
                "Sin nombre"
            );

        },


        /*
         * ==============================
         * DESCRIPCIÓN
         * ==============================
         */

        getCategoryDescription(category) {

            if (
                category.traducciones &&
                category.traducciones.length
            ) {

                const english =
                    category.traducciones.find(
                        item =>
                            item.idioma === "en"
                    );

                if (
                    english &&
                    english.descripcion
                ) {

                    return english.descripcion;

                }

            }

            if (
                category.descripcion
            ) {

                return category.descripcion;

            }

            return "Explorá las figuras de esta categoría.";

        },


        /*
         * ==============================
         * CANTIDAD DE TRUCOS
         * ==============================
         */

        getTrickCount(category) {

            if (
                category.trucos_count !== undefined
            ) {

                return category.trucos_count;

            }

            if (
                category.tricks_count !== undefined
            ) {

                return category.tricks_count;

            }

            if (
                Array.isArray(
                    category.trucos
                )
            ) {

                return category.trucos.length;

            }

            return null;

        },


        /*
         * ==============================
         * ICONOS
         * ==============================
         */

        getCategoryIcon(category) {

            const slug =
                (
                    category.slug ||
                    category.nombre ||
                    ""
                ).toLowerCase();

            if (
                slug.includes("spin") ||
                slug.includes("giro")
            ) {

                return "mdi-rotate-360";

            }

            if (
                slug.includes("climb") ||
                slug.includes("trep")
            ) {

                return "mdi-arrow-up-bold";

            }

            if (
                slug.includes("invert")
            ) {

                return "mdi-swap-vertical-bold";

            }

            if (
                slug.includes("drop")
            ) {

                return "mdi-arrow-down-bold";

            }

            if (
                slug.includes("flex")
            ) {

                return "mdi-human-handsup";

            }

            if (
                slug.includes("combo")
            ) {

                return "mdi-link-variant";

            }

            if (
                slug.includes("floor")
            ) {

                return "mdi-human";

            }

            return "mdi-star-four-points-outline";

        },


        /*
         * ==============================
         * NAVEGACIÓN CATEGORÍA
         * ==============================
         */

        goToCategory(category) {

            if (!category.slug) {

                return;

            }

            console.log(
                "Categoría seleccionada:",
                category
            );

        }

    }

};

</script>


<style scoped>

/* ======================================== */
/* SECTION */
/* ======================================== */

.level-section {

    background: #111;

    padding: 3rem 0 4rem;

}


/* ======================================== */
/* HEADER */
/* ======================================== */

.section-header {

    display: flex;

    justify-content: space-between;

    align-items: flex-end;

    gap: 2rem;

    margin-bottom: 1.5rem;

}


.section-label {

    display: block;

    margin-bottom: .35rem;

    color: #8DB8FF;

    font-size: .7rem;

    font-weight: 800;

    letter-spacing: .12em;

}


.section-header h2 {

    margin: 0;

    color: #fff;

    font-size: clamp(
        1.8rem,
        2.5vw,
        2.6rem
    );

    font-weight: 800;

    line-height: 1.1;

}


.section-description {

    margin: .45rem 0 0;

    color: #888;

    font-size: .85rem;

}


/* ======================================== */
/* VER TODAS */
/* ======================================== */

.see-all {

    flex-shrink: 0;

    padding: 0;

    border: 0;

    background: transparent;

    color: #8DB8FF;

    font-size: .8rem;

    font-weight: 700;

    cursor: pointer;

    transition: .2s;

}


.see-all:hover {

    color: white;

}


/* ======================================== */
/* GRID */
/* ======================================== */

.category-grid {

    display: grid;

    grid-template-columns:
        repeat(
            4,
            minmax(0, 1fr)
        );

    gap: .75rem;

}


/* ======================================== */
/* CATEGORY CARD */
/* ======================================== */

.category-card {

    position: relative;

    min-width: 0;

    min-height: 105px;

    display: flex;

    align-items: center;

    gap: .8rem;

    padding: 1rem;

    overflow: hidden;

    border:
        1px solid
        rgba(255,255,255,.07);

    border-radius: 15px;

    background:
        linear-gradient(
            145deg,
            #1b1b1d,
            #161618
        );

    cursor: pointer;

    transition:
        transform .22s ease,
        border-color .22s ease,
        background .22s ease;

}


.category-card:hover {

    transform:
        translateY(-3px);

    border-color:
        rgba(
            141,
            184,
            255,
            .45
        );

    background:
        linear-gradient(
            145deg,
            #202126,
            #18191c
        );

}


.category-card::before {

    content: "";

    position: absolute;

    top: 0;

    left: 15%;

    width: 70%;

    height: 1px;

    background:
        linear-gradient(
            90deg,
            transparent,
            rgba(
                141,
                184,
                255,
                .5
            ),
            transparent
        );

    opacity: 0;

    transition: .2s;

}


.category-card:hover::before {

    opacity: 1;

}


/* ======================================== */
/* ICONO */
/* ======================================== */

.category-icon {

    width: 42px;

    height: 42px;

    flex:
        0
        0
        42px;

    display: flex;

    align-items: center;

    justify-content: center;

    border-radius: 12px;

    background:
        rgba(
            141,
            184,
            255,
            .10
        );

}


.category-icon .v-icon {

    color: #8DB8FF;

    font-size: 21px;

}


/* ======================================== */
/* INFO */
/* ======================================== */

.category-info {

    flex: 1;

    min-width: 0;

}


.category-info h3 {

    margin:
        0
        0
        .2rem;

    overflow: hidden;

    color: #fff;

    font-size: .92rem;

    font-weight: 700;

    text-overflow: ellipsis;

    white-space: nowrap;

}


.category-info p {

    display: -webkit-box;

    margin: 0;

    overflow: hidden;

    color: #777;

    font-size: .68rem;

    line-height: 1.35;

    -webkit-line-clamp: 2;

    -webkit-box-orient: vertical;

}


/* ======================================== */
/* COUNT */
/* ======================================== */

.category-count {

    flex-shrink: 0;

    display: flex;

    flex-direction: column;

    align-items: center;

    padding-right: .25rem;

}


.category-count strong {

    color: #ddd;

    font-size: .82rem;

}


.category-count span {

    color: #666;

    font-size: .55rem;

}


/* ======================================== */
/* CATEGORY ARROW */
/* ======================================== */

.category-arrow {

    position: absolute;

    right: .7rem;

    bottom: .35rem;

    color:
        rgba(
            255,
            255,
            255,
            .18
        );

    font-size: 1rem;

    transition: .2s;

}


.category-card:hover
.category-arrow {

    color: #8DB8FF;

    transform:
        translateX(2px);

}


/* ======================================== */
/* ESTADOS */
/* ======================================== */

.category-state {

    min-height: 150px;

    display: flex;

    align-items: center;

    justify-content: center;

    color: #777;

    font-size: .85rem;

}


/* ======================================== */
/* A-Z */
/* ======================================== */

.az-section {

    margin-top: 4rem;

    padding:
        2.5rem
        0
        3rem;

    border-top:
        1px solid
        rgba(
            255,
            255,
            255,
            .06
        );

}


.az-count {

    padding:
        .4rem
        .8rem;

    border:
        1px solid
        rgba(
            255,
            255,
            255,
            .07
        );

    border-radius: 999px;

    background: #19191b;

    color: #777;

    font-size: .7rem;

}


/* ======================================== */
/* ALPHABET */
/* ======================================== */

.alphabet {

    display: flex;

    align-items: center;

    gap: .4rem;

    margin-bottom: 1.4rem;

    padding-bottom: .4rem;

    overflow-x: auto;

    scrollbar-width: none;

}


.alphabet::-webkit-scrollbar {

    display: none;

}


.letter {

    flex: 0 0 auto;

    min-width: 32px;

    height: 32px;

    padding:
        0
        .5rem;

    border:
        1px solid
        rgba(
            255,
            255,
            255,
            .07
        );

    border-radius: 999px;

    background: #1b1b1d;

    color: #777;

    font-size: .7rem;

    font-weight: 700;

    cursor: pointer;

    transition: .2s;

}


.letter:hover:not(.disabled) {

    background: white;

    color: #111;

}


.letter.active {

    background: white;

    color: #111;

}


.letter.disabled {

    opacity: .25;

    cursor: default;

}


.all-letter {

    padding:
        0
        .85rem;

}


/* ======================================== */
/* A-Z CAROUSEL */
/* ======================================== */

.az-carousel {

    position: relative;

}


.az-track {

    display: flex;

    gap: .85rem;

    overflow-x: auto;

    padding:
        .25rem
        0
        1rem;

    scroll-behavior: smooth;

    scroll-snap-type:
        x proximity;

    scrollbar-width: none;

}


.az-track::-webkit-scrollbar {

    display: none;

}


/* ======================================== */
/* A-Z CARD */
/* ======================================== */

.az-card {

    flex:
        0
        0
        145px;

    min-width: 0;

    cursor: pointer;

    scroll-snap-align: start;

    transition:
        transform .2s ease;

}


.az-card:hover {

    transform:
        translateY(-4px);

}


.az-image-wrapper {

    position: relative;

    width: 100%;

    aspect-ratio:
        3 / 4;

    overflow: hidden;

    border:
        1px solid
        rgba(
            255,
            255,
            255,
            .06
        );

    border-radius: 14px;

    background: #18181a;

}


.az-image {

    display: block;

    width: 100%;

    height: 100%;

    object-fit: cover;

}


.no-image {

    display: flex;

    align-items: center;

    justify-content: center;

    color: #555;

    font-size: .7rem;

}


.difficulty {

    position: absolute;

    left: .5rem;

    bottom: .5rem;

    padding:
        .25rem
        .55rem;

    border-radius: 999px;

    background:
        rgba(
            0,
            0,
            0,
            .75
        );

    color: white;

    font-size: .6rem;

    font-weight: 600;

    backdrop-filter:
        blur(8px);

}


.az-card-info {

    padding:
        .55rem
        .15rem
        0;

}


.az-card-info h3 {

    margin: 0;

    overflow: hidden;

    color: white;

    font-size: .8rem;

    font-weight: 700;

    line-height: 1.25;

    text-overflow: ellipsis;

    white-space: nowrap;

}


.az-category {

    display: block;

    margin-top: .2rem;

    color: #666;

    font-size: .63rem;

}


/* ======================================== */
/* A-Z ARROWS */
/* ======================================== */

.az-arrow {

    position: absolute;

    top: 45%;

    transform:
        translateY(-50%);

    z-index: 10;

    width: 36px;

    height: 36px;

    border:
        1px solid
        rgba(
            255,
            255,
            255,
            .1
        );

    border-radius: 50%;

    background:
        rgba(
            10,
            10,
            10,
            .9
        );

    color: white;

    cursor: pointer;

    transition: .2s;

}


.az-arrow:hover {

    background: white;

    color: #111;

}


.az-left {

    left: -18px;

}


.az-right {

    right: -18px;

}


/* ======================================== */
/* TABLET */
/* ======================================== */

@media(max-width: 960px) {

    .category-grid {

        grid-template-columns:
            repeat(
                2,
                minmax(0, 1fr)
            );

    }

}


/* ======================================== */
/* MOBILE */
/* ======================================== */

@media(max-width: 600px) {

    .level-section {

        padding:
            2rem
            0
            3rem;

    }


    .section-header {

        align-items: center;

        margin-bottom: 1rem;

    }


    .section-description {

        display: none;

    }


    .section-header h2 {

        font-size: 1.55rem;

    }


    /*
     * Categorías:
     * carrusel horizontal
     */

    .category-grid {

        display: flex;

        gap: .65rem;

        overflow-x: auto;

        margin:
            0
            -12px;

        padding:
            .25rem
            12px
            .6rem;

        scroll-snap-type:
            x proximity;

        scrollbar-width: none;

    }


    .category-grid::-webkit-scrollbar {

        display: none;

    }


    .category-card {

        flex:
            0
            0
            220px;

        min-height: 90px;

        scroll-snap-align: start;

    }


    .category-icon {

        width: 38px;

        height: 38px;

        flex-basis: 38px;

    }


    .category-info h3 {

        font-size: .85rem;

    }


    .category-info p {

        font-size: .63rem;

    }


    /*
     * A-Z
     */

    .az-section {

        margin-top: 2.5rem;

        padding-top: 1.5rem;

    }


    .az-count {

        display: none;

    }


    .az-card {

        flex-basis: 115px;

    }


    .az-arrow {

        display: none;

    }

}

</style>